import { useState } from 'react';
import { AppBar, Toolbar, Box, IconButton, Typography, Tooltip } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { DRAWER_WIDTH } from './Sidebar';
import { useAuth } from '../../context/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import { useConfirm } from '../../context/ConfirmDialogContext';
import { useThemeMode } from '../../context/ThemeContext';
import { postReboot, postShutdown } from '../../api/server';
import { PowerOverlay } from '../PowerOverlay';

export function TopBar() {
  const { auth, logout } = useAuth();
  const username = auth.status === 'authenticated' ? auth.username : null;
  const canPower = usePermission('server.power');
  const confirm = useConfirm();
  const { mode, toggleTheme } = useThemeMode();
  const [powerAction, setPowerAction] = useState<'reboot' | 'shutdown' | null>(null);

  const handleReboot = async () => {
    const ok = await confirm(
      'The server will reboot in 1 minute. All active sessions will be lost.',
      { title: 'Reboot server', confirmPhrase: 'REBOOT', confirmLabel: 'Reboot', danger: true },
    );
    if (!ok) return;
    await postReboot();
    setPowerAction('reboot');
  };

  const handleShutdown = async () => {
    const ok = await confirm(
      'The server will shut down in 1 minute. You will need physical or remote access to turn it back on.',
      { title: 'Shut down server', confirmPhrase: 'SHUTDOWN', confirmLabel: 'Shut down', danger: true },
    );
    if (!ok) return;
    await postShutdown();
    setPowerAction('shutdown');
  };

  if (powerAction) return <PowerOverlay action={powerAction} />;

  return (
    <AppBar
      position="fixed"
      color="default"
      elevation={0}
      sx={{
        width: `calc(100% - ${DRAWER_WIDTH}px)`,
        ml: `${DRAWER_WIDTH}px`,
      }}
    >
      <Toolbar sx={{ justifyContent: 'flex-end', gap: 1 }}>
        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
          <IconButton onClick={toggleTheme} color="inherit">
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>

        {canPower && (
          <>
            <Tooltip title="Reboot server">
              <IconButton onClick={() => void handleReboot()} color="warning">
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Shut down server">
              <IconButton onClick={() => void handleShutdown()} color="error">
                <PowerSettingsNewIcon />
              </IconButton>
            </Tooltip>
          </>
        )}

        {username && (
          <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary' }}>
            {username}
          </Typography>
        )}

        <Tooltip title="Logout">
          <span>
            <IconButton onClick={() => void logout()} disabled={!username}>
              <LogoutIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
}
